import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";
import "dayjs/locale/ru";
import { TranslationTree } from "@/translations";
import { CountryResponse } from "@/generated-api";
import { isIOS, isAndroid } from "react-device-detect";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

dayjs.updateLocale("en", {
  relativeTime: {
    future: "in %s",
    past: "%s ago",
    s: "seconds",
    m: "1 min",
    mm: "%d mins",
    h: "1 hr",
    hh: "%d hrs",
    d: "1 day",
    dd: "%d days",
    M: "1 mo.",
    MM: "%d mo.",
    y: "1 year",
    yy: "%d years",
  },
});

dayjs.updateLocale("ru", {
  relativeTime: {
    future: "через %s",
    past: "%s назад",
    s: "Сек.",
    m: "1 мин.",
    mm: "%d мин.",
    h: "1 час",
    hh: "%d ч.",
    d: "1 день",
    dd: "%d дней",
    M: "1 мес.",
    MM: "%d мес.",
    y: "1 год",
    yy: "%d лет",
  },
});

export function formatTimeAgo(
  dateString: string | null,
  locale: string = "en",
): string {
  if (!dateString) return "Unknown";
  return dayjs(dateString).locale(locale).fromNow();
}

export function humanize(str: string): string {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export const getTranslatedEnumLabel = (
  t: TranslationTree,
  selection: string,
): string => {
  try {
    const translation =
      t.USER_FORM.ENUM_BADGES?.[
        selection as keyof typeof t.USER_FORM.ENUM_BADGES
      ];
    if (translation && typeof translation === "string") {
      return translation;
    }
  } catch (e) {
    // If access fails, continue to fallback
  }
  return humanize(selection);
};

export function formatSubscriptionDate(
  dateString: string | null | undefined,
  locale: string = "en",
): string {
  if (!dateString) return "Not available";
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function mapAlpha2(alpha2: string) {
  const alpha2Aliases: Record<string, string> = {
    AE: "UAE",
    GB: "UK",
    US: "USA",
  };

  return alpha2Aliases[alpha2] ?? alpha2;
}

export const formatNationality = (country: CountryResponse): string => {
  const specialNationalities = new Set([
    "French",
    "Dutch",
    "Dominican",
    "Congolese",
  ]);

  if (specialNationalities.has(country.nationality)) {
    return `${country.nationality} (${country.name})`;
  }
  return country.nationality;
};

export function isHeicConversionError(
  error: unknown,
): error is { code: number; message: string } {
  if (!error || typeof error !== "object") return false;
  const errorObj = error as Record<string, unknown>;

  return (
    "code" in errorObj &&
    "message" in errorObj &&
    typeof errorObj.code === "number" &&
    typeof errorObj.message === "string"
  );
}

export function getIOSVersion(): { major: number; minor: number } | null {
  if (!isIOS || isAndroid) return null;

  let match = navigator.userAgent.match(/Version\/(\d+)\.(\d+)/);
  if (!match) {
    match = navigator.userAgent.match(/OS (\d+)[._](\d+)?/);
  }

  if (!match) return null;

  return {
    major: parseInt(match[1], 10),
    minor: parseInt(match[2] || "0", 10),
  };
}

export const REVIEWER_EMAILS = [
  "app_review@pulse-date.com",
  "app_review_onboarding@pulse-date.com",
  "app_review_unpaid@pulse-date.com",
];
